/**
 * Lógica pura de caja: saldos, libro de movimientos filtrable (también por caja) y alta/edición de cambios de moneda. Las
 * cajas en sí y las transferencias entre ellas están en `lib/cajas.ts`. Montos como
 * texto (parseMonto/montoAInput de lib/gastos). El libro y los saldos los arma el backend: acá sólo se arman
 * filtros, formularios, payloads y etiquetas.
 */
import type { OpcionSelect } from "@/components";
import type { AcreditadoMoneda, CambioMoneda, LibroCaja, Moneda, ReferenciaLinea, SaldosCaja, SentidoCambio, TipoLinea } from "./api";
import { formatMoneda, hoyISO } from "./format";
import { montoAInput, parseMonto } from "./gastos";

// ------------------------------------------------------------------ tipo de línea y filtros

export const ETIQUETA_TIPO: Record<TipoLinea, string> = {
  aporte: "Aporte",
  cobro: "Cobro",
  pago: "Pago",
  devolucion: "Devolución",
  cambio: "Cambio",
  ganancia: "Ganancia",
  transferencia: "Transferencia",
};

export const OPCIONES_TIPO: { valor: TipoLinea | ""; etiqueta: string }[] = [
  { valor: "", etiqueta: "Todos" },
  ...(Object.keys(ETIQUETA_TIPO) as TipoLinea[]).map((t) => ({ valor: t, etiqueta: ETIQUETA_TIPO[t] })),
];

export interface FiltrosCaja {
  desde: string;
  hasta: string;
  moneda: Moneda | "";
  tipo: TipoLinea | "";
  /** id de la caja como texto; "" = todas. */
  cajaId: string;
}

/** Últimos 90 días hasta hoy (o la fecha que se pase, para tests). */
export function filtrosIniciales(hoy: Date = new Date()): FiltrosCaja {
  const desde = new Date(hoy);
  desde.setDate(desde.getDate() - 90);
  return { desde: hoyISO(desde), hasta: hoyISO(hoy), moneda: "", tipo: "", cajaId: "" };
}

export function queryCaja(f: FiltrosCaja): string {
  const partes: string[] = [];
  if (f.desde) partes.push(`desde=${f.desde}`);
  if (f.hasta) partes.push(`hasta=${f.hasta}`);
  if (f.moneda) partes.push(`moneda=${f.moneda}`);
  if (f.tipo) partes.push(`tipo=${f.tipo}`);
  if (f.cajaId) partes.push(`caja_id=${f.cajaId}`);
  return partes.length ? `?${partes.join("&")}` : "";
}

export function errorFiltros(f: FiltrosCaja): string | null {
  if (f.desde && f.hasta && f.desde > f.hasta) return "La fecha 'desde' es posterior a 'hasta'";
  return null;
}

/**
 * A dónde lleva el link de "Concepto" en el libro (lo ven admin y contador). `cambio` y `transferencia` no tienen
 * ficha propia (null). Fiduciante no tiene ficha como ruta: se linkea al listado `/fiduciantes`.
 */
export function rutaReferencia(r: ReferenciaLinea | null): string | null {
  if (!r) return null;
  switch (r.tipo) {
    case "gasto":
      return `/gastos/${r.id}`;
    case "venta":
      return `/ventas/${r.id}`;
    case "fiduciante":
      return "/fiduciantes";
    case "honorarios":
      return "/honorarios";
    case "cambio":
    case "transferencia":
      return null;
  }
}

export function avisoNegativos(negativos: Moneda[]): string | null {
  if (negativos.length === 0) return null;
  const texto = negativos.length === 2 ? "pesos y en dólares" : negativos[0] === "ARS" ? "pesos" : "dólares";
  return `El saldo en ${texto} quedó negativo: falta cargar un ingreso o un cambio de moneda.`;
}

/** Formato de tipo de cambio: 4 decimales, separador de miles es-AR ("1500" → "1.500,0000"). */
const formatoTipoCambio = new Intl.NumberFormat("es-AR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });

export function formatTipoCambio(valor: string | number): string {
  return formatoTipoCambio.format(typeof valor === "string" ? Number(valor) : valor).replace(/ /g, " ");
}

/** Tarjeta de resumen para Inicio (Task 11): el total en USD, con el detalle de cada saldo por moneda. */
/** El Inicio muestra la tarjeta de caja recién cuando hay plata en alguna moneda. */
export function haySaldo(s: SaldosCaja): boolean {
  return Number(s.saldo_ars) !== 0 || Number(s.saldo_usd) !== 0;
}

export function tarjetaCaja(s: SaldosCaja): { valor: string; detalle: string } {
  return {
    valor: s.total_usd === null ? "—" : formatMoneda(s.total_usd, "USD"),
    detalle: `${formatMoneda(s.saldo_ars, "ARS")} · ${formatMoneda(s.saldo_usd, "USD")}`,
  };
}

/**
 * Detalle de la tarjeta "Total en dólares" de Caja. Si falta `total_usd` (no hay tipo de cambio ni de las líneas
 * ni de referencia), avisa. Si hay total pero no un tipo de cambio de referencia (saldo en pesos cero y ningún
 * cambio de moneda cargado todavía: ver `total_en_usd` en `app/caja/reglas.py`), no hay ningún tipo de cambio
 * real que mostrar: mejor sin leyenda que uno inventado.
 */
export function detalleTotalUsd(s: SaldosCaja): string | undefined {
  if (s.total_usd === null) return "Falta un tipo de cambio de referencia";
  if (s.tipo_cambio_referencia === null) return undefined;
  return `Pesos al tipo de cambio ${formatTipoCambio(s.tipo_cambio_referencia)}`;
}

/**
 * Lo acreditado a fiduciantes en el período sin que entre plata (pago por cuenta corriente y crédito del fee B):
 * explica por qué Aportes y Gastos no cierran con el libro. Una línea por origen con monto; vacío si no hubo.
 */
export function lineasAcreditadoSinCaja(a: LibroCaja["acreditado_sin_caja"]): string[] {
  const linea = (etiqueta: string, m: AcreditadoMoneda) =>
    Number(m.ARS) === 0 && Number(m.USD) === 0
      ? []
      : [`${etiqueta}: ${formatMoneda(m.ARS, "ARS")} · ${formatMoneda(m.USD, "USD")} · Total ${formatMoneda(m.total_usd, "USD")}`];
  return [...linea("Acreditado por cuenta corriente", a.cuenta_corriente), ...linea("Acreditado por fee B", a.fee_b)];
}

// ------------------------------------------------------------------ cambio de moneda (alta y edición)

export const ETIQUETA_SENTIDO: Record<SentidoCambio, string> = {
  venta_usd: "Venta de dólares",
  compra_usd: "Compra de dólares",
};

export const OPCIONES_SENTIDO: OpcionSelect[] = [
  { valor: "venta_usd", etiqueta: ETIQUETA_SENTIDO.venta_usd },
  { valor: "compra_usd", etiqueta: ETIQUETA_SENTIDO.compra_usd },
];

export interface FormCambio {
  fecha: string;
  sentido: SentidoCambio;
  monto_usd: string;
  monto_ars: string;
  nota: string;
  /** id de la caja como texto; "" = la principal (no se manda). */
  caja_id: string;
}

export type ErroresCambio = Partial<Record<keyof FormCambio, string>>;

export function formCambio(c?: CambioMoneda, hoy: Date = new Date(), cajaDefecto = ""): FormCambio {
  if (!c) return { fecha: hoyISO(hoy), sentido: "venta_usd", monto_usd: "", monto_ars: "", nota: "", caja_id: cajaDefecto };
  return {
    fecha: c.fecha, sentido: c.sentido, monto_usd: montoAInput(c.monto_usd), monto_ars: montoAInput(c.monto_ars), nota: c.nota ?? "", caja_id: String(c.caja_id),
  };
}

// Mismo texto que el backend (app/caja/cambios.py): así el error se ve igual, lo dispare la validación del
// cliente o la respuesta del servidor.
export function validarCambio(f: FormCambio): ErroresCambio {
  const e: ErroresCambio = {};
  if (!f.fecha) e.fecha = "Ingresá la fecha";
  if (parseMonto(f.monto_usd) === null) e.monto_usd = "El monto en USD tiene que ser mayor que cero";
  if (parseMonto(f.monto_ars) === null) e.monto_ars = "El monto en pesos tiene que ser mayor que cero";
  if (f.nota.trim().length > 200) e.nota = "Hasta 200 caracteres";
  return e;
}

/** "Tipo de cambio: …" en vivo mientras se completan los dos montos; null si falta alguno. */
export function tipoCambioCalculado(f: FormCambio): string | null {
  const usd = parseMonto(f.monto_usd);
  const ars = parseMonto(f.monto_ars);
  if (usd === null || ars === null) return null;
  return formatTipoCambio(Number(ars) / Number(usd));
}

export function aPayloadCambio(f: FormCambio): { fecha: string; sentido: SentidoCambio; monto_usd: string; monto_ars: string; nota: string | null; caja_id?: number } {
  return {
    fecha: f.fecha,
    sentido: f.sentido,
    monto_usd: parseMonto(f.monto_usd) ?? "0",
    monto_ars: parseMonto(f.monto_ars) ?? "0",
    nota: f.nota.trim() || null,
    ...(f.caja_id ? { caja_id: Number(f.caja_id) } : {}),
  };
}
